<template>
  <div
    class="form-input"
    :class="{
      'has-error': error,
      'small-input': smallInput,
      'is-compact': isCompact,
    }"
  >
    <FormLabel v-if="label" class="form-input__label" :for="name" :is-required="isRequired" :has-error="hasError">{{
      label
    }}</FormLabel>

    <textarea
      v-if="isTextarea"
      class="form-input__input form-input__textarea"
      :class="{ 'has-gray-background': hasGrayBackground, 'read-only': readonly }"
      :name="name"
      :id="name"
      :value="value"
      :rows="rows"
      :aria-required="isRequired || null"
      :aria-invalid="hasError ? 'true' : null"
      :aria-describedby="describedBy"
      @input="input"
      @change="change"
      @blur="blur"
      v-on="listeners"
      v-bind="attrs"
      :readonly="readonly"
      :placeholder="placeholder"
    />
    <input
      v-else
      class="form-input__input"
      :class="{ 'has-gray-background': hasGrayBackground, 'read-only': readonly }"
      :name="name"
      :id="name"
      :type="type"
      :value="value"
      :aria-required="isRequired || null"
      :aria-invalid="hasError ? 'true' : null"
      :aria-describedby="describedBy"
      @input="input"
      @change="change"
      @blur="blur"
      v-on="listeners"
      v-bind="attrs"
      :readonly="readonly"
      :placeholder="placeholder"
    />

    <div class="form-input__top-right" v-if="hasSlotContent">
      <slot name="top-right"></slot>
    </div>

    <span v-if="hint && !error" :id="`${name}-hint`" class="form-input__hint">{{ hint }}</span>
    <span v-if="error && !smallErrorMessage" :id="`${name}-error`" class="form-input__error-msg">{{ error }}</span>
    <span
      v-if="error && smallErrorMessage"
      class="form-input__small-error-msg"
      v-tooltip="{ content: error, classes: 'error' }"
    >
      <BaseIcon name="alert-circle" class="form-input__error-icon" />
    </span>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';
import FormLabel from '@/components/form/FormLabel';

export default {
  name: 'FormInput',
  components: {
    BaseIcon,
    FormLabel,
  },
  $_veeValidate: {
    value() {
      return this.value;
    },
    name() {
      return this.name;
    },
  },
  props: {
    name: String,
    error: {
      type: String,
      default: null,
    },
    value: {
      type: null,
      default: null,
    },
    label: String,
    isRequired: Boolean,
    type: {
      type: String,
      default: 'text',
    },
    hasGrayBackground: {
      type: Boolean,
      default: false,
      required: false,
    },
    hint: String,
    smallErrorMessage: Boolean,
    smallInput: Boolean,
    isCompact: Boolean,
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isTextarea: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [Number, String],
      default: 4,
    },
  },
  computed: {
    listeners() {
      const { input, change, blur, ...listeners } = this.$listeners;
      return listeners;
    },
    attrs() {
      const { rows, ...attrs } = this.$attrs;
      return attrs;
    },
    hasSlotContent() {
      return !!this.$slots['top-right'];
    },
    hasError() {
      return this.error != '' && this.error != null;
    },
    // Ids of the visible hint/error text so screen readers announce them with the field.
    describedBy() {
      const ids = [];
      if (this.hint && !this.hasError) ids.push(`${this.name}-hint`);
      if (this.hasError && !this.smallErrorMessage) ids.push(`${this.name}-error`);
      return ids.length ? ids.join(' ') : null;
    },
  },
  methods: {
    input(event) {
      this.$emit('input', event.target.value);
    },
    change(event) {
      this.$emit('change', event.target.value);
    },
    blur(event) {
      this.$emit('blur', event.target.value);
    },
  },
};
</script>

<style lang="scss">
.form-input {
  padding: 24px 0;
  position: relative;
  width: 100%;

  &__label {
    bottom: 64px;
    position: absolute;
    left: 0;
    top: 0;
    height: min-content;
  }

  &__top-right {
    position: absolute;
    right: 0;
    top: 0;
  }

  &__input {
    background-color: $color-white;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    color: $color-normal;
    font-size: 14px !important;
    min-height: 42px;
    outline: none;
    padding: 0 12px;
    transition: background 200ms ease, border 200ms ease, box-shadow 200ms ease;
    width: 100% !important;

    &::placeholder {
      font-style: normal;
    }

    &:hover {
      border-color: $color-normal-pale;
    }

    &:focus {
      border-color: $color-accent;
      box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1);
    }

    &.has-gray-background {
      background: $color-neutral-pale;
    }

    &.read-only {
      color: $color-neutral;
      border-color: $color-neutral-pale;
    }
  }

  // Matches the single-line input (border/radius/font/focus come from &__input); only the vertical
  // padding differs, since a multi-line field pads top/bottom instead of centering via min-height.
  &__textarea {
    padding: 10px 12px;
  }

  &__hint {
    bottom: 4px;
    color: $color-neutral;
    font-size: 12px;
    left: 0;
    position: absolute;
  }

  &__error-msg {
    bottom: 4px;
    color: $color-error;
    font-size: 12px;
    font-style: italic;
    position: absolute;
    right: 0;
  }

  &__small-error-msg {
    bottom: 37px;
    color: $color-error;
    height: 16px;
    position: absolute;
    right: -20px;
    width: 16px;
  }

  &__error-icon {
    height: 16px;
    width: 16px;
  }

  /* MODIFIERS */
  &.small-input {
    .form-input__input {
      max-width: 80px;
    }
    .form-input__small-error-msg {
      left: 86px;
      right: unset;
    }
  }

  &.is-compact {
    // Vertical breathing room only; 0 left/right so the field stays flush-left with its label,
    // matching the default's `padding: 24px 0` (the tall label/hint bands just aren't needed here).
    padding: 4px 0;

    &.has-error {
      padding-bottom: 24px;
    }

    .form-input__input {
      min-height: 32px;
    }
  }

  &.has-error {
    .form-input__input {
      border-color: $color-error;
    }
  }
}
</style>
